import { Fragment, useMemo, type CSSProperties, type ReactNode } from "react";
import { Kicker } from "@/shared/ui/primitives/Kicker";
import {
  parseMarkdown,
  type Align,
  type Block,
  type Inline,
} from "@/features/intelligence/lib/markdown";
import { fontSizeVar } from "@/shared/ui/type-scale";

const MIN_COL_PX = 120;

const PROSE_STYLE: CSSProperties = {
  fontFamily: "var(--font-display)",
  fontWeight: 400,
  fontSize: fontSizeVar("md"),
  lineHeight: "160%",
  letterSpacing: "-0.01em",
  color: "var(--color-text)",
};

const HEADING: Record<number, { size: string; weight: number; top: string }> = {
  1: { size: fontSizeVar("lg"), weight: 500, top: "24px" },
  2: { size: fontSizeVar("lg"), weight: 500, top: "24px" },
  3: { size: fontSizeVar("md"), weight: 600, top: "20px" },
  4: { size: fontSizeVar("md"), weight: 600, top: "20px" },
  5: { size: fontSizeVar("md"), weight: 500, top: "16px" },
  6: { size: fontSizeVar("md"), weight: 500, top: "16px" },
};

const CODE_STYLE: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: fontSizeVar("sm"),
  backgroundColor: "var(--color-fill-soft)",
  borderRadius: "var(--radius-sm)",
  padding: "1px 5px",
  overflowWrap: "anywhere",
  boxDecorationBreak: "clone",
  WebkitBoxDecorationBreak: "clone",
} as CSSProperties;

function withBreaks(text: string): ReactNode {
  const parts = text.split(/(?<=[_\-/])/);
  if (parts.length < 2) return text;
  return parts.map((part, i) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: these stateless fragments come from one immutable parsed text token.
    <Fragment key={i}>
      {part}
      {i < parts.length - 1 ? <wbr /> : null}
    </Fragment>
  ));
}

function alignStyle(align: Align): CSSProperties {
  return align ? { textAlign: align } : {};
}

function renderInlines(nodes: Inline[]): ReactNode {
  return nodes.map((node, i) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: parsed inline nodes are immutable and contain no component state.
    <Fragment key={i}>{renderInline(node)}</Fragment>
  ));
}

function renderInline(node: Inline): ReactNode {
  switch (node.kind) {
    case "text":
      return node.text;
    case "break":
      return <br />;
    case "strong":
      return (
        <strong className="font-medium text-[var(--color-text-strong)]">
          {renderInlines(node.children)}
        </strong>
      );
    case "em":
      return <em className="italic">{renderInlines(node.children)}</em>;
    case "del":
      return (
        <del className="text-[var(--color-text-muted)] line-through">
          {renderInlines(node.children)}
        </del>
      );
    case "code":
      return <code style={CODE_STYLE}>{withBreaks(node.text)}</code>;
    case "link":
      return (
        <a
          href={node.href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="text-[var(--color-brand)] underline underline-offset-2 [overflow-wrap:anywhere]"
        >
          {renderInlines(node.children)}
        </a>
      );
  }
}

function gapOf(block: Block, first: boolean): string {
  if (first) return "0";
  if (block.kind === "heading") return HEADING[block.level].top;
  return "16px";
}

function renderBlock(block: Block, first: boolean): ReactNode {
  const marginTop = gapOf(block, first);

  switch (block.kind) {
    case "paragraph":
      return <p style={{ marginTop }}>{renderInlines(block.children)}</p>;

    case "heading": {
      const { size, weight } = HEADING[block.level];
      const Tag = `h${block.level}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
      return (
        <Tag
          style={{
            marginTop,
            fontFamily: "var(--font-display)",
            fontWeight: weight,
            fontSize: size,
            lineHeight: "140%",
            letterSpacing: "-0.01em",
            color: "var(--color-text-strong)",
          }}
        >
          {renderInlines(block.children)}
        </Tag>
      );
    }

    case "rule":
      return (
        <hr
          style={{ marginTop, marginBottom: "0" }}
          className="border-0 border-t border-[var(--color-border-strong)]"
        />
      );

    case "code":
      return (
        <pre
          style={{
            marginTop,
            fontFamily: "var(--font-mono)",
            fontSize: fontSizeVar("sm"),
            lineHeight: "160%",
            backgroundColor: "var(--color-fill-soft)",
            borderRadius: "var(--radius-md)",
            padding: "12px",
            color: "var(--color-text-strong)",
          }}
          className="max-w-full overflow-x-auto"
        >
          <code>{block.text}</code>
        </pre>
      );

    case "list": {
      const Tag = block.ordered ? "ol" : "ul";
      return (
        <Tag
          start={block.ordered ? block.start : undefined}
          style={{ marginTop, paddingInlineStart: "22px" }}
          className={block.ordered ? "list-decimal" : "list-disc"}
        >
          {block.items.map((item, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: parsed Markdown list items are immutable and contain no component state.
            <li key={i} style={{ marginTop: i === 0 ? "0" : "6px", paddingInlineStart: "2px" }}>
              {item.map((child, j) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: parsed Markdown child blocks are immutable and contain no component state.
                <Fragment key={j}>
                  {child.kind === "paragraph" && item.length === 1 ? (
                    renderInlines(child.children)
                  ) : (
                    <div style={{ marginTop: j === 0 ? "0" : "6px" }}>
                      {renderBlock(child, true)}
                    </div>
                  )}
                </Fragment>
              ))}
            </li>
          ))}
        </Tag>
      );
    }

    case "table":
      return (
        <div style={{ marginTop }} className="max-w-full overflow-x-auto">
          <table
            className="w-full border-collapse"
            style={{ tableLayout: "fixed", minWidth: `${block.head.length * MIN_COL_PX}px` }}
          >
            <thead>
              <tr className="border-b border-[var(--color-border-strong)]">
                {block.head.map((cell, i) => (
                  <th
                    // biome-ignore lint/suspicious/noArrayIndexKey: parsed Markdown columns are immutable and contain no component state.
                    key={i}
                    className="px-[8px] py-[6px] align-bottom [overflow-wrap:anywhere]"
                    style={{ textAlign: block.align[i] ?? "left" }}
                  >
                    <Kicker tone="faint">{renderInlines(cell)}</Kicker>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: parsed Markdown rows are immutable and contain no component state.
                <tr key={r} className="border-b border-[var(--color-border)]">
                  {row.map((cell, c) => (
                    <td
                      // biome-ignore lint/suspicious/noArrayIndexKey: parsed Markdown cells are immutable and contain no component state.
                      key={c}
                      className="px-[8px] py-[8px] align-top [overflow-wrap:anywhere]"
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: fontSizeVar("md"),
                        lineHeight: "150%",
                        color: "var(--color-text)",
                        ...alignStyle(block.align[c]),
                      }}
                    >
                      {renderInlines(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

export interface MarkdownProseProps {
  text: string;
}

export function MarkdownProse({ text }: MarkdownProseProps) {
  const blocks = useMemo(() => parseMarkdown(text), [text]);

  return (
    <div className="min-w-0" style={PROSE_STYLE}>
      {blocks.map((block, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: blocks are reparsed as an immutable, stateless tree whenever text changes.
        <Fragment key={i}>{renderBlock(block, i === 0)}</Fragment>
      ))}
    </div>
  );
}
